import { z } from "zod";

// Only static raster/vector images are allowed for tenant assets — no animated
// GIFs and no video. Keep this in sync with ALLOWED_IMAGE_ACCEPT below.
export const ALLOWED_IMAGE_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/bmp",
  "image/svg+xml",
] as const;

// `accept` attribute for the file input — restricts the native picker.
export const ALLOWED_IMAGE_ACCEPT =
  ".jpg,.jpeg,.png,.webp,.bmp,.svg,image/jpeg,image/png,image/webp,image/bmp,image/svg+xml";

export const INVALID_IMAGE_MESSAGE =
  "Only static image files are allowed (JPG, PNG, WEBP, BMP, SVG). GIFs and videos are not allowed.";

export const isStaticImageFile = (file: File): boolean =>
  ALLOWED_IMAGE_MIME_TYPES.includes(file.type as (typeof ALLOWED_IMAGE_MIME_TYPES)[number]);

export const tenantAssetCreateSchema = z.object({
  tenantId: z.string().min(1, "Tenant ID is required"),
  assetType: z.enum(["logo", "background", "widget", "report_logo"], {
    required_error: "Asset type is required",
    invalid_type_error:
      "Asset type must be logo, background, widget, or report_logo",
  }),
  file: z
    .any()
    .refine((val) => val instanceof FileList && val.length > 0, {
      message: "File is required",
    })
    .refine(
      // Only validate the type when a file is present so the "File is required"
      // message above takes precedence on an empty selection.
      (val) =>
        !(val instanceof FileList) ||
        val.length === 0 ||
        isStaticImageFile(val[0]),
      { message: INVALID_IMAGE_MESSAGE }
    ),
  metadata: z.record(z.any()).optional(),
});

export type TenantAssetCreateFormData = z.infer<
  typeof tenantAssetCreateSchema
>;

export const tenantAssetUpdateSchema = z.object({
  file: z
    .instanceof(File)
    .refine((f) => isStaticImageFile(f), { message: INVALID_IMAGE_MESSAGE })
    .optional(),
  metadata: z.record(z.any()).optional(),
});

export type TenantAssetUpdateFormData = z.infer<
  typeof tenantAssetUpdateSchema
>;

export interface TenantAsset {
  id: string;
  tenantId: string;
  assetType: string;
  fileName: string;
  fileUrl: string;
  format: string;
  thumbnailUrl: string | null;
  metadata: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
}

export interface TenantAssetResult {
  assets: TenantAsset[];
}

export interface CreateTenantAssetInput {
  assetType: "logo" | "background" | "widget" | "report_logo";
  file: File;
  metadata?: Record<string, any>;
}

export interface CreateTenantAssetPayload {
  input: CreateTenantAssetInput;
}

// Input for update mutation (matches UpdateTenantAssetInput)
export interface UpdateTenantAssetInput {
  file?: File;
  metadata?: Record<string, any>;
}

export interface UpdateTenantAssetPayload {
  id: string;
  input: UpdateTenantAssetInput;
}

export interface DeleteTenantAssetPayload {
  id: string;
}
